import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import moment from "moment";
import DataTable from "react-data-table-component";
import { handleNotify, TOASTER_TYPE, TOASTER_POSITION } from "../../common/notification/toaster_notify.component";
import { InvoiceService } from "../../../services/user/invoices.services";
import { useSelector } from "react-redux";
import { LeadsService } from "../../../services/user/leads.services";
import { StatusService } from "../../../services/user/statuses.services";
import { UserServices } from "../../../services/user/user.services";
import MainItemDetailsModal from "../quotes/mainitempopup.component";
import DatePicker from "react-datepicker";
//import { Editor } from "@tinymce/tinymce-react";
import { getDateFormat, parseCultureDate, VALIDITY_ENUM } from "../../../utility";
import StandardItemsModal from "./standarditempopup.component";
import striptags from "striptags";
import Select from 'react-select';
import ItemModal from "../invoice/itemmodalpopup";
import { Editor } from "@tinymce/tinymce-react";

const AddInvoiceComponent = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const invoiceId = location.state?.invoiceId || 0;
    const isEditMode = !!invoiceId;
    const selectedStore = useSelector((state) => state?.user?.selectedStoreId);
    const [loading, setLoading] = useState(false);
    const [lead, setLead] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [users, setUsers] = useState([]);
    const [showMainItemModal, setShowMainItemModal] = useState(false);
    const [mainItemData, setMainItemData] = useState([]);
    const [showStandardModal, setShowStandardModal] = useState(false);
    const [standardItemData, setStandardItemData] = useState([]);
    const [leadOptions, setLeadOptions] = useState([]);
    const [showOptionalModal, setShowOptionalModal] = useState(false);
    const [childType, setChildType] = useState(null);
    const [optionalItemData, setOptionalItemData] = useState([])


    const validityOptions = VALIDITY_ENUM.map(validity => ({
        value: validity.validityName,
        label: validity.validityName
    }));

    const formik = useFormik({
        initialValues: {
            invoiceSubject: "",
            createDate: new Date(),
            userId: 0,
            validity: VALIDITY_ENUM[0].validityName,
            prefixNumber: "",
            privateNotes: "",
            expirationDate: new Date(),
            statusId: 0,
            leadId: 0,
            address: "",
            mainItemId: 0,
            categoryId: 0,
            standardItemId: [],
            optionalItemId: [],
            invoiceDescription: "",
            termsAndConditions: "",
            total: 0,
            cgstRate: "",
            sgstRate: "",
            igstRate: "",
            mainItemPrice: "",
            mainItemQuantity: "",
            mainItemAmount: '',
            mainItemDiscountRate: "",
            mainItemDiscountAmount: "",
            mainItemSubTotal: "",
            mainItemGstAmount: "",
            mainItemTotal: "",
            mainItemCGSTAmount: "",
            mainItemSGSTAmount: "",
            mainItemIGSTAmount: "",
        },
        validationSchema: Yup.object({
            invoiceSubject: Yup.string().required("Please Enter Subject"),
        }),
        onSubmit: async (values) => {
            try {
                if (!values.mainItemId || values.mainItemId === 0) {
                    handleNotify("Please select main item.", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                    return;
                }
                let clearDescription = striptags(values.invoiceDescription);
                let clearTermsAndCondition = striptags(values.termsAndConditions);
                const data = {
                    ...values,
                    createDate: values.createDate ? new Date(values.createDate).toISOString() : null,
                    expirationDate: values.expirationDate ? new Date(values.expirationDate).toISOString() : null,
                    userId: Number(values.userId),
                    statusId: Number(values.statusId),
                    leadId: Number(values.leadId),
                    invoiceDescription: clearDescription,
                    termsAndConditions: clearTermsAndCondition
                };
                setLoading(true);
                if (isEditMode) {
                    await InvoiceService.updateInvoiceById(invoiceId, data);
                    navigate("/invoice");
                } else {
                    await InvoiceService.addNewInvoice(data);
                    navigate("/invoice");
                }
            } catch (error) {
                handleNotify("An error occurred while saving the invoice.", TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
            } finally {
                setLoading(false);
            }
        },
    });

    const calculateExpirationDate = (startDate, validityPeriod) => {
        if (!startDate || !validityPeriod) return null;
        const validityDuration = parseInt(validityPeriod, 10);
        return moment(startDate).add(validityDuration, "days").toDate();
    };

    useEffect(() => {
        if (formik.values.createDate && formik.values.validity) {
            const newExpirationDate = calculateExpirationDate(
                formik.values.createDate,
                formik.values.validity
            );
            formik.setFieldValue("expirationDate", newExpirationDate);
        }
    }, [formik.values.createDate, formik.values.validity]);

    useEffect(() => {
        if (isEditMode) {
            const fetchInvoiceDetail = async () => {
                try {
                    const data = await InvoiceService.getInvoiceById(invoiceId);
                    formik.setValues({
                        leadId: data?.leadId,
                        invoiceSubject: data?.invoiceSubject,
                        createDate: parseCultureDate(data?.createDate),
                        // createDate: moment(data?.createDate).format('YYYY-MM-DD'),
                        address: data?.address,
                        userId: data?.userId,
                        validity: data?.validity,
                        prefixNumber: data?.prefixNumber,
                        privateNotes: data?.privateNotes,
                        expirationDate: parseCultureDate(data?.expirationDate),
                        //  expirationDate: moment(data?.expirationDate).format('YYYY-MM-DD'),
                        statusId: data?.statusId,
                        mainItemId: data?.mainItemId,
                        categoryId: data?.categoryId,
                        standardItemId: data?.standardItemId,
                        optionalItemId: data?.optionalItemId,
                        invoiceDescription: data?.invoiceDescription,
                        termsAndConditions: data?.termsAndConditions,
                        total: data?.total,
                    });

                    // Process the main items for the grid
                    const mainItems = data?.invoiceMainItems?.map(item => {
                        // Convert the tax rates and amounts to proper numbers
                        const cgstRate = parseFloat(item.cgstRate) || 0;
                        const sgstRate = parseFloat(item.sgstRate) || 0;
                        const igstRate = parseFloat(item.igstRate) || 0;

                        const price = parseFloat(item.price) || 0;
                        const quantity = parseFloat(item.quantity) || 0;
                        const discountRate = parseFloat(item.discountRate) || 0;
                        const discountAmount = parseFloat(item.discountAmount) || 0;
                        const gstTotal = parseFloat(item.gstTotal) || 0;
                        const mainItemTotal = parseFloat(item.mainItemTotal) || 0;

                        const cgstAmount = price * cgstRate / 100;
                        const sgstAmount = price * sgstRate / 100;
                        const igstAmount = price * igstRate / 100;
                        const amount = price * quantity;

                        // Calculate GST and total
                        const gstAmount = cgstAmount + sgstAmount + igstAmount;
                        const total = amount - discountAmount + gstAmount;

                        return {
                            itemId: item.itemId,
                            itemName: item.itemName,
                            itemImage: item.itemImage,
                            cgst: cgstRate,
                            sgst: sgstRate,
                            igst: igstRate,
                            price: price,
                            quantity: quantity,
                            amount: amount,
                            discountRate: discountRate,
                            discountAmount: discountAmount,
                            gstAmount: gstAmount,
                            total: total,
                        };
                    });

                    setMainItemData(mainItems);
                    const leadResponse = await LeadsService.getAllLeads(selectedStore);
                    const selectedLead = leadResponse.find(lead => lead.leadId === data?.leadId);

                    if (selectedLead) {
                        formik.setFieldValue('address', selectedLead.address.addressLine1 + ", " + selectedLead.address.city);
                    }
                } catch (error) {
                    handleNotify("Error fetching invoice details", error, TOASTER_TYPE.ERROR, TOASTER_POSITION.TOP_RIGHT);
                }
            };
            fetchInvoiceDetail();
        }
    }, []);

    const handleLeadChange = (selectedOption) => {
        const selectedLead = lead.find((lead) => lead.leadId === selectedOption.value);
        formik.setFieldValue('leadId', selectedOption.value);

        if (selectedLead && selectedLead.address) {
            formik.setFieldValue('address', `${selectedLead.address.addressLine1}, ${selectedLead.address.city}`)
        }
    }

    const fetchLeadData = async () => {
        const leadResponse = await LeadsService.getAllLeads(selectedStore);
        const sortedLead = leadResponse.sort((a, b) => a.leadId - b.leadId);
        const leadOptions = sortedLead.map((lead) => ({
            value: lead.leadId,
            label: lead.companyName,
        }));
        setLead(sortedLead);
        setLeadOptions(leadOptions)

        if (sortedLead.length > 0) {
            formik.setFieldValue('leadId', sortedLead[0]?.leadId);
            formik.setFieldValue('address',
                `${sortedLead[0]?.address?.addressLine1 || ''}, ${sortedLead[0]?.address?.city || ''}`);
        }
    };

    useEffect(() => {
        fetchLeadData();
    }, []);

    const fetchStatusData = async () => {
        const statusResponse = await StatusService.getStatusByType("Invoice");
        const sortedStatus = statusResponse.sort((a, b) => a.statusId - b.statusId);
        const statusIdOptions = sortedStatus.map(status => ({
            value: status.statusId,
            label: status.statusName,
        }));

        setStatuses(statusIdOptions);
        if (!formik.values.statusId && statusIdOptions.length > 0) {
            formik.setFieldValue('statusId', statusIdOptions[0].value);
        }
    };

    useEffect(() => {
        fetchStatusData();
    }, []);

    const fetchUsersData = async () => {
        const userResponse = await UserServices.getAllUsers(selectedStore);
        const sortedUsers = userResponse.sort((a, b) => a.userId - b.userId);
        const userIdOptions = sortedUsers.map(user => ({
            value: user.userId,
            label: `${user.username}(${user.role.roleName})`,
        }))
        formik.setFieldValue('userId', sortedUsers[0]?.userId);
        setUsers(userIdOptions);
        if (!formik.values.userId && userIdOptions.length > 0) {
            formik.setFieldValue('userId', userIdOptions[0].value);
        }
    };

    useEffect(() => {
        fetchUsersData();
    }, []);

    // //Date calculation 
    // const calculateExpirationDate = (startDate, validityPeriod) => {
    //     if (!startDate || !validityPeriod) return null;

    //     const validityDuration = parseInt(validityPeriod, 10);
    //     const expirationDate = moment(startDate).add(validityDuration, "days");
    //     return expirationDate.format("YYYY-MM-DD");
    // };

    // useEffect(() => {
    //     if (formik.values.createDate && formik.values.validity) {
    //         const newExpirationDate = calculateExpirationDate(formik.values.createDate, formik.values.validity);
    //         formik.setFieldValue("expirationDate", newExpirationDate);
    //     }
    // }, [formik.values.createDate, formik.values.validity]);

    // // Handlers for date changes
    // const handleCreateDateChange = (val) => {
    //     const formattedCreatedDate = moment(val).toISOString();
    //     formik.setFieldValue("createDate", formattedCreatedDate);
    // };

    // const handleExpirationDateChange = (val) => {
    //     const formattedExpirationDate = moment(val).toISOString();
    //     formik.setFieldValue("expirationDate", formattedExpirationDate);
    // };

    const handleAddItemClick = () => {
        setShowMainItemModal(true)
    };

    const handleBackClick = () => {
        navigate("/invoice");
    };

    const calculateTaxes = (selectedItem) => {
        //debugger
        const { intraStateTaxGroup } = selectedItem;

        let cgst = 0, sgst = 0, igst = 0, quantity = 1, discountRate = 0, discountAmount = 0;

        if (intraStateTaxGroup) {
            cgst = parseFloat(intraStateTaxGroup.CGST) || 0;
            sgst = parseFloat(intraStateTaxGroup.SGST) || 0;
            igst = parseFloat(intraStateTaxGroup.IGST) || 0;
        }

        const price = selectedItem.price || 0;
        const amount = price * quantity;
        discountAmount = (amount * discountRate) / 100;
        const cgstAmount = (amount * cgst) / 100;
        const sgstAmount = (amount * sgst) / 100;
        const igstAmount = (amount * igst) / 100;
        const gstAmount = cgstAmount + sgstAmount + igstAmount;
        const subTotal = amount - discountAmount;
        const total = subTotal + gstAmount;

        return {
            cgst, sgst, igst, quantity, amount, cgstAmount, sgstAmount, igstAmount, discountAmount, discountRate, gstAmount, subTotal, total
        };
    };

    // standerdItem Calculations
    const calculateItemDetails = (item) => {
        const { price, quantity, cgstRate, sgstRate, igstRate, discountRate } = item;

        const priceValue = parseFloat(price);
        const quantityValue = parseInt(quantity);
        const cgstRateValue = parseFloat(cgstRate);
        const sgstRateValue = parseFloat(sgstRate);
        const igstRateValue = parseFloat(igstRate);
        const discountRateValue = parseFloat(discountRate);

        // Calculate amounts
        const amount = priceValue * quantityValue;
        const discountAmount = (amount * discountRateValue) / 100;
        const cgstAmount = (amount * cgstRateValue) / 100;
        const sgstAmount = (amount * sgstRateValue) / 100;
        const igstAmount = (amount * igstRateValue) / 100;

        const gstAmount = cgstAmount + sgstAmount + igstAmount;
        const standardItemSubTotal = amount - discountAmount;
        const standardItemTotal = standardItemSubTotal + gstAmount;

        return {
            amount,
            discountAmount,
            cgstAmount,
            sgstAmount,
            igstAmount,
            gstAmount,
            standardItemSubTotal,
            standardItemTotal
        };
    };

    const handleItemSelect = (selectedItems) => {
        if (!Array.isArray(selectedItems)) {
            console.error('selectedItems is not an array', selectedItems);
            return;
        }

        if (childType === "Standard") {
            let updatedStandardItemIds = [...formik.values.standardItemId];
            selectedItems.forEach((selectedItem) => {
                const taxDetails = calculateItemDetails(selectedItem);
                const itemWithTaxDetails = { ...selectedItem, ...taxDetails };

                if (!updatedStandardItemIds.includes(selectedItem.itemId)) {
                    updatedStandardItemIds.push(selectedItem.itemId);
                }

                setStandardItemData((prevItems) => {
                    const existingItem = prevItems.find((item) => item.itemId === itemWithTaxDetails.itemId);
                    if (!existingItem) {
                        return [...prevItems, itemWithTaxDetails];
                    }
                    return prevItems;
                });

                formik.setFieldValue('standardItemId', updatedStandardItemIds);
            });
        } else if (childType === "Optional") {
            let updatedOptionalItemIds = [...formik.values.optionalItemId];
            selectedItems.forEach((selectedItem) => {
                const taxDetails = calculateItemDetails(selectedItem);
                const itemWithTaxDetails = { ...selectedItem, ...taxDetails };

                if (!updatedOptionalItemIds.includes(selectedItem.itemId)) {
                    updatedOptionalItemIds.push(selectedItem.itemId);
                }

                setOptionalItemData((prevItems) => {
                    const existingItem = prevItems.find((item) => item.itemId === itemWithTaxDetails.itemId);
                    if (!existingItem) {
                        return [...prevItems, itemWithTaxDetails];
                    }
                    return prevItems;
                });

                formik.setFieldValue('optionalItemId', updatedOptionalItemIds);
            });
        }

        setShowStandardModal(false);
        setShowOptionalModal(false);
    };

    //handleStandardItemSelect
    const handleStandardItemSelect = (selectedItems) => {
        if (selectedItems && selectedItems.length > 0) {
            const selectedStandardItem = selectedItems[0];
            setFormikValuesForItem(selectedStandardItem);
            // Update the standard items data
            setStandardItemData((prevItems) => {
                const existingItem = prevItems.find((item) => item.itemId === selectedStandardItem.itemId);
                if (!existingItem) {
                    return [...prevItems, selectedStandardItem];
                }
                return prevItems;
            });

            setShowStandardModal(false);
        }
    };
    const setFormikValuesForItem = (item) => {
        formik.setFieldValue('standardItemId', 7);//item.id
        formik.setFieldValue('standardIteminvoiceId', 15);//item.invoiceId
        formik.setFieldValue('standardItemName', "VTissue");//item.itemName
        formik.setFieldValue('standardCgstRate', "6.00");// item.cgstRate
        formik.setFieldValue('standardSgstRate', "6.00");// item.sgstRate
        formik.setFieldValue('standardIgstRate', "0.00");// item.igstRate
        //  const price = (typeof item.price === 'number' && !isNaN(item.price)) ? item.price.toFixed(2) : '0.00';
        formik.setFieldValue('standardPrice', "0.00");//price
        formik.setFieldValue('standardQuantity', 1);//item.quantity || 0
        formik.setFieldValue('standardItemAmount', "0.00");//item.standardItemAmount || 0
        formik.setFieldValue('standardDiscountRate', "0.00");//item.discountRate || 0
        formik.setFieldValue('standardDiscountAmount', "0.00");//item.discountAmount || 0
        formik.setFieldValue('standardItemSubTotal', "0.00");//item.standardItemSubTotal || 0
        formik.setFieldValue('standardCgstAmount', "0.00");// item.cgstAmount || 0
        formik.setFieldValue('standardSgstAmount', "0.00");//item.sgstAmount || 0
        formik.setFieldValue('standardIgstAmount', "0.00");//item.igstAmount || 0
    };
    // const setFormikValuesForItem = (item) => {
    //     formik.setFieldValue('standardItemId', item.id);
    //     formik.setFieldValue('standardIteminvoiceId', item.invoiceId);
    //     formik.setFieldValue('standardItemName', item.itemName);
    //     formik.setFieldValue('standardCgstRate', item.cgstRate);
    //     formik.setFieldValue('standardSgstRate', item.sgstRate);
    //     formik.setFieldValue('standardIgstRate', item.igstRate);
    //     const price = (typeof item.price === 'number' && !isNaN(item.price)) ? item.price.toFixed(2) : '0.00';
    //     formik.setFieldValue('standardPrice', price);
    //     formik.setFieldValue('standardQuantity', item.quantity || 0);
    //     formik.setFieldValue('standardItemAmount', item.standardItemAmount || 0);
    //     formik.setFieldValue('standardDiscountRate', item.discountRate || 0);
    //     formik.setFieldValue('standardDiscountAmount', item.discountAmount || 0);
    //     formik.setFieldValue('standardItemSubTotal', item.standardItemSubTotal || 0);
    //     formik.setFieldValue('standardCgstAmount', item.cgstAmount || 0);
    //     formik.setFieldValue('standardSgstAmount', item.sgstAmount || 0);
    //     formik.setFieldValue('standardIgstAmount', item.igstAmount || 0);
    // };


    //OptionalItem Details 
    const calculateOptionalItemDetails = (item) => {
        const { price, quantity, cgstRate, sgstRate, igstRate, discountRate } = item;

        const priceValue = parseFloat(price);
        const quantityValue = parseInt(quantity);
        const cgstRateValue = parseFloat(cgstRate);
        const sgstRateValue = parseFloat(sgstRate);
        const igstRateValue = parseFloat(igstRate);
        const discountRateValue = parseFloat(discountRate);

        // Calculate amounts
        const amount = priceValue * quantityValue;
        const discountAmount = (amount * discountRateValue) / 100;
        const cgstAmount = (amount * cgstRateValue) / 100;
        const sgstAmount = (amount * sgstRateValue) / 100;
        const igstAmount = (amount * igstRateValue) / 100;

        const gstAmount = cgstAmount + sgstAmount + igstAmount;
        const optionalItemSubTotal = amount - discountAmount;
        const optionalItemTotal = optionalItemSubTotal + gstAmount;

        return {
            amount,
            discountAmount,
            cgstAmount,
            sgstAmount,
            igstAmount,
            gstAmount,
            optionalItemSubTotal,
            optionalItemTotal
        };
    };

    //handleOptionalItemSelect
    const handleOptionalItemSelect = (selectedItems) => {
        const selectedOptionalItem = selectedItems[0];
        if (selectedOptionalItem) {
            formik.setFieldValue('optionalId', selectedOptionalItem.id);
            formik.setFieldValue('optionalItemInvoiceId', selectedOptionalItem.invoiceId);
            formik.setFieldValue('optionalItemName', selectedOptionalItem.itemName);
            formik.setFieldValue('optionalItemId', selectedOptionalItem.itemId);
            formik.setFieldValue('optionalCgstRate', selectedOptionalItem.cgstRate);
            formik.setFieldValue('optionalSgstRate', selectedOptionalItem.sgstRate);
            formik.setFieldValue('optionalIgstRate', selectedOptionalItem.igstRate);
            formik.setFieldValue('optionalPrice', selectedOptionalItem.price);
            formik.setFieldValue('optionalQuantity', selectedOptionalItem.quantity);
            formik.setFieldValue('optionalItemAmount', selectedOptionalItem.standardItemAmount);
            formik.setFieldValue('optionalDiscountRate', selectedOptionalItem.discountRate);
            formik.setFieldValue('optionalDiscountAmount', selectedOptionalItem.discountAmount);
            formik.setFieldValue('optionalItemSubTotal', selectedOptionalItem.standardItemSubTotal);
            formik.setFieldValue('optionalCgstAmount', selectedOptionalItem.cgstAmount);
            formik.setFieldValue('optionalSgstAmount', selectedOptionalItem.sgstAmount);
            formik.setFieldValue('optionalIgstAmount', selectedOptionalItem.igstAmount);
        }
        setOptionalItemData(selectedItems);
    };


    const handleMainItemSelect = (selectedItems) => {
        const selectedItem = selectedItems[0];
        if (selectedItem) {
            const taxDetails = calculateTaxes(selectedItem);

            const itemWithTaxDetails = {
                ...selectedItem,
                discountRate: 0,
                ...taxDetails
            };

            formik.setFieldValue('mainItemId', selectedItem.itemId);
            formik.setFieldValue('categoryId', selectedItem.categoryId);
            formik.setFieldValue('cgstRate', selectedItem.intraStateTaxGroup.CGST);
            formik.setFieldValue('sgstRate', selectedItem.intraStateTaxGroup.SGST);
            formik.setFieldValue('igstRate', selectedItem.intraStateTaxGroup.IGST);
            formik.setFieldValue('mainItemPrice', selectedItem.price);
            formik.setFieldValue('mainItemQuantity', taxDetails.quantity);
            formik.setFieldValue('mainItemAmount', taxDetails.amount);
            formik.setFieldValue('mainItemDiscountRate', 0); // Default to 0
            formik.setFieldValue('mainItemDiscountAmount', taxDetails.discountAmount);
            formik.setFieldValue('mainItemSubTotal', taxDetails.subTotal);
            formik.setFieldValue('mainItemCGSTAmount', taxDetails.cgstAmount);
            formik.setFieldValue('mainItemSGSTAmount', taxDetails.sgstAmount);
            formik.setFieldValue('mainItemIGSTAmount', taxDetails.igstAmount);
            formik.setFieldValue('mainItemGstAmount', taxDetails.gstAmount);
            formik.setFieldValue(`mainItemTotal`, taxDetails.total);
            formik.setFieldValue('total', taxDetails.total);

            // Add item to the grid data
            setMainItemData(prevItems => {
                const existingItem = prevItems.find(item => item.itemId === itemWithTaxDetails.itemId);
                if (!existingItem) {
                    return [...prevItems, itemWithTaxDetails];
                }
                return prevItems;
            });

            formik.setFieldValue('total', itemWithTaxDetails.total);
        }
        setShowMainItemModal(false);
    };

    const handleFieldChange = (itemId, field, value) => {
        setMainItemData(prevItems => {
            return prevItems.map(item => {
                if (item.itemId === itemId) {
                    item[field] = parseFloat(value) || 0;
                    item.amount = item.price * item.quantity;
                    item.discountAmount = (item.amount * item.discountRate) / 100;
                    item.cgstAmount = (item.amount * item.cgst) / 100;
                    item.sgstAmount = (item.amount * item.sgst) / 100;
                    item.igstAmount = (item.amount * item.igst) / 100;
                    item.gstAmount = item.cgstAmount + item.sgstAmount + item.igstAmount;
                    item.subTotal = item.amount + item.gstAmount
                    item.total = item.subTotal - item.discountAmount;

                    // Update the corresponding formik values after recalculating
                    formik.setFieldValue(`mainItemDiscountRate`, item.discountRate);
                    formik.setFieldValue(`mainItemDiscountAmount`, item.discountAmount);
                    formik.setFieldValue(`mainItemQuantity`, item.quantity);
                    formik.setFieldValue(`mainItemAmount`, item.amount);
                    formik.setFieldValue(`mainItemSubTotal`, item.subTotal);
                    formik.setFieldValue(`mainItemCGSTAmount`, item.cgstAmount);
                    formik.setFieldValue(`mainItemSGSTAmount`, item.sgstAmount);
                    formik.setFieldValue(`mainItemIGSTAmount`, item.igstAmount);
                    formik.setFieldValue(`mainItemGstAmount`, item.gstAmount);
                    formik.setFieldValue(`mainItemTotal`, item.total);
                    formik.setFieldValue(`total`, item.total);
                }
                return item;
            });
        });
    };

    const handleMainItemDeleteClick = (itemId) => {
        setMainItemData(prevItems => {
            const updatedItem = prevItems.filter(item => item.itemId !== itemId);
            if (updatedItem.length === 0) {
                formik.setFieldValue('mainItemId', 0);
                formik.setFieldValue('categoryId', 0);
            }

            return updatedItem;
        });
    };

    const handleStandardItemsClick = () => {
        setShowStandardModal(true);
        setChildType("Standard");
    };

    const handleOptionalItemsClick = () => {
        setShowOptionalModal(true);
        setChildType("Optional");
    };

    const getImageFromBuffer = (buffer) => {
        if (buffer?.data) {
            const blob = new Blob([new Uint8Array(buffer.data)], { type: 'image/jpeg' });
            return URL.createObjectURL(blob);
        }
        return '';
    };

    const isAddMainItemButtonDisabled = mainItemData.length > 0;
    const isStandardAndOptionalItemButtonDisabled = mainItemData.length === 0;

    const handleStandardItemDeleteClick = (itemId) => {
        setStandardItemData((prevData) => prevData.filter(item => item.itemId !== itemId));

        const deleteItem = async () => {
            try {
                const response = await InvoiceService.deleteStandardItem(invoiceId, itemId);
                if (response.success) {
                    console.log("Standard item deleted successfully");
                } else {
                    console.error("Failed to delete standard item");
                }
            } catch (error) {
                console.error("Error deleting standard item", error);
            }
        };
        deleteItem();
    };

    const handleOptionalItemDeleteClick = (itemId) => {
        setOptionalItemData(prevItems => {
            const updatedItem = prevItems.filter(item => item.itemId !== itemId);
            if (updatedItem.length === 0) {
                formik.setFieldValue('standardItemId', 0);
            }

            return updatedItem;
        });
    };

    const mainItemsColumns = [
        { name: "Image", selector: (row) => <img src={getImageFromBuffer(row?.itemImage)} style={{ width: "50px", height: "50px" }} />, sortable: true, width: '7%' },
        { name: "Item Name", selector: (row) => <input className="form-control" type="text" value={row.itemName} readOnly />, sortable: true, width: '10%' },
        { name: "CGST(%)", selector: (row) => <input className="form-control" type="text" value={row.cgst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "SGST(%)", selector: (row) => <input className="form-control" type="text" value={row.sgst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "IGST(%)", selector: (row) => <input className="form-control" type="text" value={row.igst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "Price", selector: (row) => <input className="form-control" type="number" value={row.price} onChange={(e) => handleFieldChange(row.itemId, 'price', e.target.value)} min="0.00" step="0.01" />, sortable: true, width: '9%' },
        { name: "Qty", selector: (row) => <input className="form-control" type="number" value={row.quantity.toFixed(2)} onChange={(e) => handleFieldChange(row.itemId, 'quantity', e.target.value)} min="1" />, sortable: true, width: '7%' },
        { name: "Amount(₹)", selector: (row) => <input className="form-control" type="text" value={row.amount.toFixed(2)} readOnly />, width: '7%' },

        // Make discountRate editable
        {
            name: "Discount(%)",
            selector: (row) => (
                <input
                    className="form-control"
                    type="number"
                    value={row.discountRate.toFixed(2)}
                    onChange={(e) => handleFieldChange(row.itemId, 'discountRate', e.target.value)}
                    min="0"
                    step="0.01"
                />
            ),
            width: '8%'
        },

        { name: "Discount(₹)", selector: (row) => <input className="form-control" type="text" value={row.discountAmount.toFixed(2)} readOnly />, width: '8%' },
        { name: "GST(₹)", selector: (row) => <input className="form-control" type="text" value={row.gstAmount.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "Total", selector: (row) => <input className="form-control" type="text" value={row.total.toFixed(2)} readOnly />, sortable: true, width: '8%' },
        { name: "Actions", button: true, width: "10%", cell: (row) => <div className="d-flex justify-content-center gap-2"><button className="btn btn-danger" onClick={() => handleMainItemDeleteClick(row.itemId)}>Delete</button></div> },
    ];


    const standardItemColumn = [
        { name: "Image", selector: (row) => <img src={getImageFromBuffer(row?.itemImage)} style={{ width: "50px", height: "50px" }} />, sortable: true, width: '7%' },
        { name: "Item Name", selector: (row) => <input className="form-control" type="text" value={row.itemName} readOnly />, sortable: true, width: '10%' },
        { name: "CGST(%)", selector: (row) => <input className="form-control" type="text" value={row.cgst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "SGST(%)", selector: (row) => <input className="form-control" type="text" value={row.sgst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "IGST(%)", selector: (row) => <input className="form-control" type="text" value={row.igst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "price", selector: (row) => <input className="form-control" type="number" value={row.price} onChange={(e) => handleFieldChange(row.itemId, 'price', e.target.value)} min="0.00" step="0.01" />, sortable: true, width: '9%' },
        { name: "Qty", selector: (row) => <input className="form-control" type="number" value={row.quantity.toFixed(2)} onChange={(e) => handleFieldChange(row.itemId, 'quantity', e.target.value)} min="1" />, sortable: true, width: '7%' },
        { name: "Amount(₹)", selector: (row) => <input className="form-control" type="text" value={row.amount.toFixed(2)} readOnly />, width: '7%' },
        { name: "Discount(%)", selector: (row) => <input className="form-control" type="text" value={row.discountRate.toFixed(2)} readOnly />, width: '8%' },
        { name: "Discount(₹)", selector: (row) => <input className="form-control" type="text" value={row.discountAmount.toFixed(2)} readOnly />, width: '8%' },
        { name: "GST(₹)", selector: (row) => <input className="form-control" type="text" value={row.gstAmount.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "Total", selector: (row) => <input className="form-control" type="text" value={row.total.toFixed(2)} readOnly />, sortable: true, width: '8%' },
        { name: "Actions", button: true, width: "10%", cell: (row) => <div className="d-flex justify-content-center gap-2"><button className="btn btn-danger" onClick={() => handleStandardItemDeleteClick(row.itemId)}>Delete</button></div> },
    ];

    const optionalItemsColumns = [
        { name: "Image", selector: (row) => <img src={getImageFromBuffer(row?.itemImage)} style={{ width: "50px", height: "50px" }} />, sortable: true, width: '7%' },
        { name: "Item Name", selector: (row) => <input className="form-control" type="text" value={row.itemName} readOnly />, sortable: true, width: '10%' },
        { name: "CGST(%)", selector: (row) => <input className="form-control" type="text" value={row.cgst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "SGST(%)", selector: (row) => <input className="form-control" type="text" value={row.sgst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "IGST(%)", selector: (row) => <input className="form-control" type="text" value={row.igst.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "price", selector: (row) => <input className="form-control" type="number" value={row.price} onChange={(e) => handleFieldChange(row.itemId, 'price', e.target.value)} min="0.00" step="0.01" />, sortable: true, width: '9%' },
        { name: "Qty", selector: (row) => <input className="form-control" type="number" value={row.quantity.toFixed(2)} onChange={(e) => handleFieldChange(row.itemId, 'quantity', e.target.value)} min="1" />, sortable: true, width: '7%' },
        { name: "Amount(₹)", selector: (row) => <input className="form-control" type="text" value={row.amount.toFixed(2)} readOnly />, width: '7%' },
        { name: "Discount(%)", selector: (row) => <input className="form-control" type="text" value={row.discountRate.toFixed(2)} readOnly />, width: '8%' },
        { name: "Discount(₹)", selector: (row) => <input className="form-control" type="text" value={row.discountAmount.toFixed(2)} readOnly />, width: '8%' },
        { name: "GST(₹)", selector: (row) => <input className="form-control" type="text" value={row.gstAmount.toFixed(2)} readOnly />, sortable: true, width: '7%' },
        { name: "Total", selector: (row) => <input className="form-control" type="text" value={row.total.toFixed(2)} readOnly />, sortable: true, width: '8%' },
        { name: "Actions", button: true, width: "10%", cell: (row) => <div className="d-flex justify-content-center gap-2"><button className="btn btn-danger" onClick={() => handleOptionalItemDeleteClick(row.itemId)}>Delete</button></div> },
    ];

    // console.log(formik.errors)

    return (
        <div className="page-wrapper m-0">
            <div className="page-content">
                <div className="page-breadcrumb d-flex align-items-center mb-3">
                    <div
                        className="breadcrumb-title pe-3 text-primary fw-bold fs-4 cursor-pointer"
                        onClick={handleBackClick}
                    >
                        Invoices
                    </div>
                    <div className="ps-3">
                        <nav aria-label="breadcrumb">
                            <ol className="breadcrumb mb-0 p-0">
                                <li className="breadcrumb-item active" aria-current="page">
                                    {isEditMode ? "Edit Invoice" : "New Invoice"}
                                </li>
                            </ol>
                        </nav>
                    </div>
                    <div className="ms-auto">
                        <button
                            type="button"
                            className="btn btn-outline-secondary me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                    </div>
                </div>
                <form onSubmit={formik.handleSubmit}>
                    <div className="card radius-10 border-top border-0 border-4 border-primary">
                        <div className="card-body p-5">
                            <div className="row mb-4">
                                {/* Left Side Card */}
                                <div className="col-md-6 col-12">
                                    <div className="card p-4 border-top-0 border-3 border-primary">
                                        <div className="card-body card-body-border">
                                            <div className="row mb-4">
                                                <div className="col-md-12 col-6">
                                                    <label htmlFor="invoiceSubject" className="form-label d-flex">
                                                        Subject<span className="text-danger">*</span>:
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="invoiceSubject"
                                                        name="invoiceSubject"
                                                        placeholder="Enter invoiceSubject"
                                                        className={`form-control ${formik.touched.invoiceSubject && formik.errors.invoiceSubject ? "is-invalid" : ""}`}
                                                        value={formik.values.invoiceSubject}
                                                        onChange={formik.handleChange}
                                                    />
                                                    {formik.touched.invoiceSubject && formik.errors.invoiceSubject && (
                                                        <div className="invalid-feedback d-flex">{formik.errors.invoiceSubject}</div>
                                                    )}
                                                </div>
                                            </div>
                                            {/* Lead Select Field */}
                                            <div className="row mb-4">
                                                <div className="col-md-12 col-6">
                                                    <label htmlFor="leadId" className="form-label d-flex">
                                                        Select Lead Customer:
                                                    </label>
                                                    <div className="text-start">
                                                        <Select
                                                            id="leadId"
                                                            name="leadId"
                                                            options={leadOptions}
                                                            value={leadOptions.find(option => option.value === formik.values.leadId)}
                                                            onChange={handleLeadChange}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="row mb-4">
                                                    <div className="col-md-12 col-6">
                                                        <small className="d-flex">
                                                            Change the customer, all added items will be removed
                                                        </small>
                                                        <div className="mt-3">
                                                            <textarea
                                                                id="address"
                                                                name="address"
                                                                value={formik.values.address}
                                                                rows={5}
                                                                onChange={formik.handleChange}
                                                                placeholder="Enter Address"
                                                                className="form-control"
                                                            />
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* Right Side Card */}
                                <div className="col-md-6 col-12">
                                    <div className="card p-4 border-top-0 border-3 border-primary">
                                        <div className="card-body card-body-border">
                                            <div className="row mb-4">
                                                <div className="col-md-6 col-6">
                                                    <label htmlFor="createDate" className="form-label d-flex">
                                                        Date Created:
                                                    </label>
                                                    <div className="d-flex row">
                                                        <DatePicker
                                                            selected={parseCultureDate(formik.values.createDate)}
                                                            onChange={(date) => formik.setFieldValue("createDate", date)}
                                                            onBlur={formik.handleBlur}
                                                            name="createDate"
                                                            dateFormat={getDateFormat()}
                                                            className={`form-control ${formik.touched.createDate && formik.errors.createDate ? 'is-invalid' : ''}`}
                                                            id="createDate"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-6">
                                                    <label htmlFor="validity" className="form-label d-flex">
                                                        Validity:
                                                    </label>
                                                    <div className="text-start">
                                                        <Select
                                                            id="validity"
                                                            name="validity"
                                                            options={validityOptions}
                                                            value={validityOptions.find(option => option.value === formik.values.validity)}
                                                            onChange={selectedOption => formik.setFieldValue('validity', selectedOption.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row mb-4">
                                                <div className="col-md-6 col-6">
                                                    <label htmlFor="expirationDate" className="form-label d-flex">
                                                        Expiry Date:
                                                    </label>
                                                    <div className="d-flex row">
                                                        <DatePicker
                                                            selected={formik.values.expirationDate}
                                                            onChange={(date) => formik.setFieldValue("expirationDate", date)}
                                                            onBlur={formik.handleBlur}
                                                            name="expirationDate"
                                                            dateFormat={getDateFormat()}
                                                            // dateFormat="dd-MM-yyyy"
                                                            className={`form-control ${formik.touched.expirationDate && formik.errors.expirationDate ? 'is-invalid' : ''}`}
                                                            id="expirationDate"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-6">
                                                    <label htmlFor="statusId" className="form-label d-flex">
                                                        Status:
                                                    </label>
                                                    <div className="text-start">
                                                        <Select
                                                            id="statusId"
                                                            name="statusId"
                                                            options={statuses}
                                                            value={statuses.find(option => option.value === formik.values.statusId)}
                                                            onChange={selectedOption => formik.setFieldValue('statusId', selectedOption.value)}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                            <div className="row mb-4">
                                                <div className="col-md-6 col-6">
                                                    <label htmlFor="userId" className="form-label d-flex">
                                                        Assigned To:
                                                    </label>
                                                    <div className="text-start">
                                                        <Select
                                                            id="userId"
                                                            name="userId"
                                                            options={users}
                                                            value={users.find(option => option.value === formik.values.userId)}
                                                            onChange={selectedOption => formik.setFieldValue('userId', selectedOption.value)}
                                                        />
                                                    </div>
                                                </div>
                                                <div className="col-md-6 col-6">
                                                    <label htmlFor="prefixNumber" className="form-label d-flex">
                                                        Number:
                                                    </label>
                                                    <input
                                                        type="text"
                                                        id="prefixNumber"
                                                        name="prefixNumber"
                                                        placeholder="Enter prefixNumber"
                                                        className="form-control"
                                                        value={formik.values.prefixNumber}
                                                        onChange={formik.handleChange}
                                                    />
                                                </div>
                                            </div>
                                            <div className="row mb-4">
                                                <div className="col-md-12 col-6">
                                                    <label htmlFor="privateNotes" className="form-label d-flex">
                                                        Private Notes:
                                                    </label>
                                                    <textarea
                                                        id="privateNotes"
                                                        name="privateNotes"
                                                        rows={1}
                                                        placeholder="Enter Private Notes"
                                                        className="form-control"
                                                        value={formik.values.privateNotes}
                                                        onChange={formik.handleChange}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                    {/*  for main item  */}
                    <div className="card radius-10 border-top border-0 border-4 border-primary mt-5">
                        <div className="card-body p-5">
                            <div>
                                <div className="page-breadcrumb d-flex align-items-center mb-3">
                                    <div class="font-22 text-primary p-1"><i class="fadeIn animated bx bx-stats"></i>
                                    </div>
                                    <div className="pe-3 text-primary fw-bold fs-4">Main Item</div>
                                    <div className="ms-auto">
                                        <button
                                            type="button"
                                            className="btn btn-primary px-3 py-2 rounded-3 shadow-sm hover-effect"
                                            onClick={handleAddItemClick}
                                            disabled={isAddMainItemButtonDisabled}
                                        >
                                            Add <i class="fadeIn animated bx bx-plus"></i>
                                        </button>
                                    </div>
                                </div>
                                <div className="table-responsive mb-4">
                                    <DataTable
                                        columns={mainItemsColumns}
                                        data={mainItemData}
                                        fixedHeader
                                        responsive
                                        highlightOnHover
                                        sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                        progressPending={loading}
                                    />
                                </div>
                            </div>

                            <hr />

                            <div>
                                <div className="row">
                                    <div className="page-breadcrumb d-flex align-items-center mt-4">
                                        <div class="font-22 text-primary p-1"><i class="fadeIn animated bx bx-stats"></i>
                                        </div>
                                        <div className="pe-3 text-primary fw-bold fs-4">Standard Items</div>
                                        <div className="ms-auto">
                                            <button
                                                type="button"
                                                className="btn btn-primary px-3 py-2 rounded-3 shadow-sm hover-effect"
                                                onClick={handleStandardItemsClick}
                                                disabled={isStandardAndOptionalItemButtonDisabled}
                                            >
                                                Add <i class="fadeIn animated bx bx-plus"></i>
                                            </button>
                                        </div>
                                    </div>

                                    <div className="table-responsive mb-4">
                                        <DataTable
                                            columns={standardItemColumn}
                                            data={standardItemData}
                                            fixedHeader
                                            responsive
                                            highlightOnHover
                                            sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                            progressPending={loading}
                                        />
                                    </div>
                                </div>

                                <div className="row">
                                    <div className="col-6 col-md-6 col-lg-6"></div>
                                    <div className="col-6 col-md-6 col-lg-6">
                                        <div className="row mb-3 text-start">
                                            <div className="col"></div>
                                            <div className="col text-danger fw-bold">
                                                Main Item
                                            </div>
                                            <div className="col text-dark fw-bold">
                                                Total
                                            </div>
                                        </div>

                                        {mainItemData.map(item => (
                                            <div key={item.itemId}>
                                                {/* Dynamically render the fields */}
                                                {[
                                                    { label: 'Item(Qty)', field: 'quantity' },
                                                    { label: 'Amount(₹)', field: 'amount' },
                                                    { label: 'CGST(₹)', field: 'cgstAmount' },
                                                    { label: 'SGST(₹)', field: 'sgstAmount' },
                                                    { label: 'IGST(₹)', field: 'igstAmount' },
                                                    { label: 'Total GST(₹)', field: 'gstAmount' },
                                                    { label: 'Sub Total(₹)', field: 'subTotal' },
                                                    { label: 'Discount(₹)', field: 'discountAmount' },
                                                    { label: 'Grand Total(₹)', field: 'total' }
                                                ].map(({ label, field }) => (
                                                    <div className="row mb-3 text-end" key={field}>
                                                        <div className="col">{label}</div>
                                                        {/* Column for mainItem (Editable) */}
                                                        <div className="col">
                                                            <input
                                                                type="number"
                                                                className="form-control bg-light text-end"
                                                                value={item[field] !== undefined ? item[field].toFixed(2) : '0.00'} // Format to 2 decimals
                                                                onChange={(e) => handleFieldChange(item.itemId, field, e.target.value)}
                                                                placeholder="0.00"
                                                                readOnly
                                                            />
                                                        </div>
                                                        {/* Column for total (Readonly) */}
                                                        <div className="col">
                                                            <input
                                                                type="text"
                                                                className="form-control bg-light text-end"
                                                                value={item[field] !== undefined ? item[field].toFixed(2) : '0.00'} // Format to 2 decimals
                                                                readOnly
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ))}

                                    </div>
                                </div>
                            </div>
                            <hr />
                            <div className="row">
                                <div className="page-breadcrumb d-flex align-items-center mt-4">
                                    <div class="font-22 text-primary p-1"><i class="fadeIn animated bx bx-stats"></i>
                                    </div>
                                    <div className="pe-3 text-primary fw-bold fs-4">Optional Items</div>
                                    <div className="ms-auto">
                                        <button
                                            type="button"
                                            className="btn btn-primary px-3 py-2 rounded-3 shadow-sm hover-effect"
                                            onClick={handleOptionalItemsClick}
                                            disabled={isStandardAndOptionalItemButtonDisabled}
                                        >
                                            Add <i class="fadeIn animated bx bx-plus"></i>
                                        </button>
                                    </div>
                                </div>

                                <div className="table-responsive mb-4">
                                    <DataTable
                                        columns={optionalItemsColumns}
                                        data={optionalItemData}
                                        fixedHeader
                                        responsive
                                        highlightOnHover
                                        sortIcon={<i className="fadeIn animated bx bx-sort" />}
                                        progressPending={loading}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* for description termsAndCondition */}
                    <div className="card radius-10 border-top border-0 border-4 border-primary mt-5">
                        <div className="card-body p-5">
                            <div className="border border-gray p-3 rounded-2">
                                <div className="row mt-2">
                                    <div className="col-12">
                                        <label htmlFor="invoiceDescription" className="form-label d-flex">
                                            Description :
                                        </label>
                                        <Editor
                                            value={formik.values.invoiceDescription}
                                            tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                                            init={{
                                                height: 300,
                                                menubar: true,
                                                plugins: [
                                                    "advlist autolink lists link image charmap print preview anchor",
                                                    "searchreplace visualblocks code fullscreen",
                                                    "insertdatetime media table paste code help wordcount",
                                                ],
                                                toolbar:
                                                    "undo redo | formatselect | bold italic backcolor | \
                                                   alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                   link image",
                                            }}
                                            onEditorChange={(content) =>
                                                formik.setFieldValue("invoiceDescription", content)
                                            }
                                        />
                                    </div>
                                </div>

                                <div className="row mt-3">
                                    <div className="col-12">
                                        <label htmlFor="termsAndConditions" className="form-label d-flex">
                                            Terms And Condition :
                                        </label>
                                        <Editor
                                            value={formik.values.termsAndConditions}
                                            tinymceScriptSrc={`${process.env.PUBLIC_URL}/tinymce/tinymce.min.js`}
                                            init={{
                                                height: 300,
                                                menubar: true,
                                                plugins: [
                                                    "advlist autolink lists link image charmap print preview anchor",
                                                    "searchreplace visualblocks code fullscreen",
                                                    "insertdatetime media table paste code help wordcount",
                                                ],
                                                toolbar:
                                                    "undo redo | formatselect | bold italic backcolor | \
                                                   alignleft aligncenter alignright | bullist numlist outdent indent | \
                                                   link image",
                                            }}
                                            onEditorChange={(content) =>
                                                formik.setFieldValue("termsAndConditions", content)
                                            }
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Submit Button  */}
                    <hr />
                    <div className="d-flex flex-column flex-md-row mt-4 justify-content-end">
                        <button
                            type="button"
                            className="btn btn-outline-secondary mb-2 mb-md-0 me-2 px-4 py-2 rounded-3 shadow-sm hover-effect"
                            onClick={handleBackClick}
                        >
                            Back
                        </button>
                        <button
                            type="submit"
                            className="btn btn-primary px-4 py-2 rounded-3 shadow-sm hover-effect"
                            disabled={loading}
                        >
                            {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
                        </button>
                    </div>
                    <MainItemDetailsModal
                        showMainItemModal={showMainItemModal}
                        setShowMainItemModal={setShowMainItemModal}
                        handleMainItemSelect={handleMainItemSelect}
                    />
                    {/* <StandardItemsModal
                        showStandardModal={showStandardModal}
                        setShowStandardModal={setShowStandardModal}
                        handleStandardItemSelect={handleStandardItemSelect}
                        itemId={formik.values.mainItemId}
                    /> */}

                    <ItemModal
                        showModal={showStandardModal} // Standard modal 
                        setShowModal={setShowStandardModal}
                        //handleItemSelect={handleItemSelect}
                        handleItemSelect={handleStandardItemSelect}
                        itemId={formik.values.mainItemId}
                        childType="Standard"
                    />

                    <ItemModal
                        showModal={showOptionalModal} // Optional modal 
                        setShowModal={setShowOptionalModal}
                        handleItemSelect={handleItemSelect}
                        itemId={formik.values.mainItemId}
                        childType="Optional"
                    />

                </form>
            </div>
        </div>
    );
};

export default AddInvoiceComponent;
